// src/components/HR/components/Pages/Dashboard.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer
} from 'recharts';
import {
  Users, CheckCircle, AlertCircle, Calendar,
  CalendarCheck, DollarSign, UserPlus, AlertTriangle, TrendingUp, Info, Download, Star, Play
} from 'lucide-react';

import Card from '../Card';
import KPICard from '../Dashboard/KPICard';
import Modal from '../UI/Modal';
import { getStoredRole, getStoredUser, hrApi } from '../../../api';
import { getOrgId } from '../../utils/auth';

// ── Skeleton shimmer block ──────────────────────────────────────────────────
const Shimmer = ({ width = '100%', height = 16, radius = 6, style = {} }) => (
  <span className="skeleton" style={{ width, height, borderRadius: radius, ...style }} />
);

const KPISkeleton = () => (
  <div style={{
    background: 'white', borderRadius: 14, padding: 20,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 10
  }}>
    <Shimmer width={40} height={40} radius={10} />
    <Shimmer width="55%" height={32} radius={6} />
    <Shimmer width="70%" height={13} radius={4} />
  </div>
);

const ChartSkeleton = () => (
  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 280, padding: '8px 8px 0' }}>
    {[55, 80, 45, 90, 60, 75, 40].map((h, i) => (
      <div key={i} style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 3 }}>
        <Shimmer height={h * 2.2} style={{ flex: 1, borderRadius: '4px 4px 0 0' }} />
        <Shimmer height={h * 1.4} style={{ flex: 1, borderRadius: '4px 4px 0 0' }} />
      </div>
    ))}
  </div>
);

const RatingsTableSkeleton = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    {[1, 2, 3, 4, 5].map((row) => (
      <div
        key={row}
        style={{
          display: 'grid',
          gridTemplateColumns: '1.4fr 1fr 0.8fr 1fr 1fr 1fr 0.9fr',
          gap: 10,
          padding: '10px 0',
          borderBottom: '1px solid #f1f5f9'
        }}
      >
        <Shimmer height={13} width="80%" />
        <Shimmer height={13} width="65%" />
        <Shimmer height={13} width="60%" />
        <Shimmer height={13} width="75%" />
        <Shimmer height={13} width="72%" />
        <Shimmer height={13} width="68%" />
        <Shimmer height={13} width="62%" />
      </div>
    ))}
  </div>
);

const PartialStars = ({ value = 0, size = 16 }) => {
  const safeValue = Math.max(0, Math.min(5, Number(value || 0)));
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {[0, 1, 2, 3, 4].map((idx) => {
        const fillPct = Math.max(0, Math.min(1, safeValue - idx)) * 100;
        return (
          <div key={idx} style={{ position: 'relative', width: size, height: size }}>
            <Star size={size} color="#d1d5db" />
            <div style={{ position: 'absolute', inset: 0, width: `${fillPct}%`, overflow: 'hidden' }}>
              <Star size={size} color="#f59e0b" fill="#f59e0b" />
            </div>
          </div>
        );
      })}
      <span style={{ fontSize: 12, color: '#475569', fontWeight: 700 }}>{safeValue.toFixed(2)}</span>
    </div>
  );
};

// ── Quick Action Button ─────────────────────────────────────────────────────
const QuickBtn = ({ icon: Icon, label, color, bg, onClick, fullWidth = false }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        gridColumn: fullWidth ? 'span 2' : undefined,
        display: 'flex', flexDirection: fullWidth ? 'row' : 'column',
        alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: fullWidth ? '12px 16px' : '16px 10px',
        borderRadius: 12, border: fullWidth ? 'none' : `1.5px solid ${hovered ? color : '#e8edf2'}`,
        background: fullWidth ? (hovered ? '#1a1a1a' : '#2d2d2d') : (hovered ? `${bg}` : 'white'),
        color: fullWidth ? 'white' : (hovered ? color : '#4b5563'),
        fontSize: fullWidth ? 14 : 12, fontWeight: 700,
        cursor: 'pointer', transition: 'all 0.2s',
        boxShadow: hovered ? '0 4px 12px rgba(0,0,0,0.12)' : '0 1px 4px rgba(0,0,0,0.04)'
      }}
    >
      <Icon size={fullWidth ? 17 : 21} color={fullWidth ? 'white' : (hovered ? color : color)} />
      {label}
    </button>
  );
};

// ── Dashboard ───────────────────────────────────────────────────────────────
const Dashboard = ({ setActivePage, userRole, canWrite: canWriteProp }) => {
  const org_id = getOrgId();
  const [chartView, setChartView] = useState('Weekly');

  const [kpis, setKpis] = useState({ totalWorkers: 0, presentToday: 0, absentToday: 0, onLeaveToday: 0 });
  const [chartData, setChartData] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [systemAlert, setSystemAlert] = useState(null);

  const [kpiLoading, setKpiLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [ratingsMonth, setRatingsMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [ratingsRows, setRatingsRows] = useState([]);
  const [ratingsLoading, setRatingsLoading] = useState(true);
  const [ratingsRunLoading, setRatingsRunLoading] = useState(false);
  const [ratingsError, setRatingsError] = useState('');
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [expiringDocsLoading, setExpiringDocsLoading] = useState(true);
  const [isExpiringDocsModalOpen, setIsExpiringDocsModalOpen] = useState(false);

  const isAdmin = useMemo(() => {
    try {
      const user = getStoredUser() || {};
      const allRoles = new Set([
        ...(Array.isArray(user.roles) ? user.roles : []),
        user.active_role,
        user.primary_role,
        getStoredRole()
      ].filter(Boolean).map((r) => String(r).toUpperCase()));
      return allRoles.has('ADMIN') || allRoles.has('ORG_ADMIN');
    } catch {
      return false;
    }
  }, []);

  const loadSummary = useCallback(async () => {
    setKpiLoading(true);
    try {
      const res = await hrApi.get(`/orgs/${org_id}/hr/dashboard/summary`);
      setKpis(res.data);
    } catch (e) {
      console.error('Dashboard summary error:', e);
    } finally {
      setKpiLoading(false);
    }
  }, [org_id]);

  const loadTrend = useCallback(async (view) => {
    setChartLoading(true);
    try {
      const res = await hrApi.get(`/orgs/${org_id}/hr/dashboard/attendance-trend`, { params: { view } });
      setChartData(res.data);
    } catch (e) {
      console.error('Dashboard trend error:', e);
    } finally {
      setChartLoading(false);
    }
  }, [org_id]);

  const loadAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const res = await hrApi.get(`/orgs/${org_id}/hr/dashboard/alerts`);
      setAlerts(res.data.alerts || []);
      setSystemAlert(res.data.system_alert);
    } catch (e) {
      console.error('Dashboard alerts error:', e);
    } finally {
      setAlertsLoading(false);
    }
  }, [org_id]);

  const loadExpiringDocs = async () => {
    setExpiringDocsLoading(true);
    try {
      const res = await hrApi.get('/hr/documents/expiring-soon');
      setExpiringDocs(res.data?.data || []);
    } catch (e) {
      console.error('Expiring documents error:', e);
      setExpiringDocs([]);
    } finally {
      setExpiringDocsLoading(false);
    }
  };

  const loadMonthlyRatings = useCallback(async (targetMonth = ratingsMonth) => {
    if (!org_id) return;
    setRatingsLoading(true);
    setRatingsError('');
    try {
      const res = await hrApi.get(`/orgs/${org_id}/hr/monthly-ratings`, { params: { month: targetMonth } });
      setRatingsRows(res?.data?.data || []);
    } catch (e) {
      console.error('Monthly ratings error:', e);
      setRatingsRows([]);
      setRatingsError(e?.response?.data?.message || 'Unable to load monthly ratings.');
    } finally {
      setRatingsLoading(false);
    }
  }, [org_id, ratingsMonth]);

  const runMonthlyRatings = async () => {
    if (!org_id) return;
    setRatingsRunLoading(true);
    setRatingsError('');
    try {
      await hrApi.post(`/orgs/${org_id}/workers/run-monthly-ratings`, { month: ratingsMonth });
      await loadMonthlyRatings(ratingsMonth);
    } catch (e) {
      console.error('Run monthly ratings error:', e);
      setRatingsError(e?.response?.data?.message || 'Failed to run monthly ratings.');
    } finally {
      setRatingsRunLoading(false);
    }
  };

  const downloadRatingsCsv = () => {
    if (!ratingsRows.length) return;
    const headers = ['Worker', 'Department', 'Stars', 'Pieces Completed', 'QC Pass %', 'Attendance %', 'Score'];
    const lines = ratingsRows.map((row) => [
      `"${(row.worker_name || '').replace(/"/g, '""')}"`,
      `"${(row.department || '-').replace(/"/g, '""')}"`,
      Number(row.stars || 0).toFixed(2),
      Number(row.pieces_completed || 0),
      Number(row.qc_pass_rate || 0).toFixed(2),
      Number(row.attendance_rate || 0).toFixed(2),
      Number(row.score || 0).toFixed(2)
    ].join(','));
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `hr-monthly-ratings-${ratingsMonth}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => { loadSummary(); loadAlerts(); loadExpiringDocs(); }, [loadAlerts, loadSummary]);
  useEffect(() => { loadTrend(chartView); }, [chartView, loadTrend]);
  useEffect(() => { loadMonthlyRatings(ratingsMonth); }, [loadMonthlyRatings, ratingsMonth]);

  const attendanceRate = kpis.totalWorkers > 0
    ? Math.round((kpis.presentToday / kpis.totalWorkers) * 100)
    : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── KPI Cards ─────────────────────────────────────────────────── */}
      <div className="dashboard-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
        {kpiLoading ? (
          [1, 2, 3, 4].map(i => <KPISkeleton key={i} />)
        ) : (
          <>
            <KPICard
              title="Total Workers"
              value={kpis.totalWorkers}
              icon={Users}
              trend="Click to view all"
              onClick={() => setActivePage('employees')}
            />
            <KPICard
              title="Present Today"
              value={kpis.presentToday}
              icon={CheckCircle}
              trend={`${attendanceRate}% attendance rate`}
              onClick={() => setActivePage('attendance')}
            />
            <KPICard
              title="Absent Today"
              value={kpis.absentToday}
              icon={AlertCircle}
              trend="Not checked in"
              onClick={() => setActivePage('attendance')}
            />
            <KPICard
              title="On Leave"
              value={kpis.onLeaveToday}
              icon={Calendar}
              trend="Approved leaves today"
              onClick={() => setActivePage('attendance')}
            />
          </>
        )}
      </div>

      {/* ── Main Two-Column Grid ───────────────────────────────────────── */}
      <button
        onClick={() => setIsExpiringDocsModalOpen(true)}
        style={{
          border: '1px solid #fdba74',
          background: expiringDocs.length > 0 ? '#fff7ed' : '#f8fafc',
          color: expiringDocs.length > 0 ? '#9a3412' : '#475569',
          borderRadius: 12,
          padding: '11px 14px',
          fontSize: 13,
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          width: 'fit-content',
          cursor: 'pointer'
        }}
      >
        <AlertTriangle size={15} />
        Documents Expiring Soon: {expiringDocsLoading ? '...' : expiringDocs.length}
      </button>

      <div className="two-col-grid">

        {/* Attendance Trends Chart */}
        <Card
          title="Attendance Trends"
          action={
            <div style={{ display: 'flex', background: '#f1f5f9', padding: 3, borderRadius: 8 }}>
              {['Weekly', 'Monthly'].map(view => (
                <button
                  key={view}
                  onClick={() => setChartView(view)}
                  style={{
                    border: 'none',
                    background: chartView === view ? '#fff' : 'transparent',
                    color: chartView === view ? '#1e293b' : '#94a3b8',
                    padding: '5px 14px', borderRadius: 6,
                    fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    boxShadow: chartView === view ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  {view}
                </button>
              ))}
            </div>
          }
        >
          {chartLoading ? (
            <ChartSkeleton />
          ) : chartData.length === 0 ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', height: 280, color: '#cbd5e1', gap: 10
            }}>
              <TrendingUp size={44} strokeWidth={1} />
              <div style={{ fontSize: 14, color: '#94a3b8', fontWeight: 500 }}>
                No trend data available yet — attendance will appear here.
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} barGap={3} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                <Tooltip
                  cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                  contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                />
                <Bar dataKey="Present" fill="#F7C873" radius={[5, 5, 0, 0]} name="Present" />
                <Bar dataKey="Absent"  fill="#fca5a5" radius={[5, 5, 0, 0]} name="Absent"  />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Quick Actions & Alerts */}
        <Card title="Quick Actions">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Action Buttons */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <QuickBtn
                icon={CalendarCheck}
                label="Mark Attendance"
                color="#4f46e5"
                bg="#eef2ff"
                onClick={() => setActivePage('attendance')}
              />
              <QuickBtn
                icon={DollarSign}
                label="Pay Workers"
                color="#059669"
                bg="#f0fdf4"
                onClick={() => setActivePage('payroll')}
              />
              <QuickBtn
                icon={UserPlus}
                label="Add New Worker"
                color="#fff"
                bg="#1a1a1a"
                onClick={() => setActivePage('employees')}
                fullWidth
              />
            </div>

            {/* Alerts Section */}
            <div>
              <div style={{
                fontSize: 12, fontWeight: 700, color: '#94a3b8',
                textTransform: 'uppercase', letterSpacing: '0.06em',
                marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6
              }}>
                <AlertTriangle size={13} color="#f59e0b" /> Attention Needed
              </div>

              {alertsLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Shimmer height={52} radius={8} />
                  <Shimmer height={52} radius={8} />
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {alerts.length === 0 && !systemAlert ? (
                    <div style={{
                      padding: '14px 16px', background: '#f0fdf4',
                      border: '1px solid #bbf7d0', borderRadius: 10,
                      fontSize: 13, color: '#166534',
                      display: 'flex', alignItems: 'center', gap: 10
                    }}>
                      <CheckCircle size={17} />
                      <span>All attendance levels healthy — no alerts.</span>
                    </div>
                  ) : (
                    <>
                      {alerts.map((a, i) => (
                        <div key={i} style={{
                          padding: '12px 14px', background: '#fffbeb',
                          borderLeft: '3px solid #fbbf24', borderRadius: 8,
                          fontSize: 13, color: '#92400e'
                        }}>
                          <div style={{ fontWeight: 700, marginBottom: 2 }}>⚠ Alert</div>
                          {a.message}
                        </div>
                      ))}
                      {systemAlert && (
                        <div style={{
                          padding: '12px 14px', background: '#eff6ff',
                          borderLeft: '3px solid #3b82f6', borderRadius: 8,
                          fontSize: 13, color: '#1d4ed8',
                          display: 'flex', gap: 8
                        }}>
                          <Info size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                          <span><strong>System:</strong> {systemAlert}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      <Card
        title="Monthly Ratings"
        action={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="month"
              value={ratingsMonth}
              onChange={(e) => setRatingsMonth(e.target.value)}
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '7px 10px',
                fontSize: 13,
                color: '#334155',
                minHeight: 38
              }}
            />
            <button
              onClick={downloadRatingsCsv}
              disabled={!ratingsRows.length}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                border: '1px solid #cbd5e1', background: '#fff',
                borderRadius: 8, padding: '7px 12px',
                fontSize: 12, fontWeight: 700, color: ratingsRows.length ? '#334155' : '#94a3b8',
                cursor: ratingsRows.length ? 'pointer' : 'not-allowed',
                minHeight: 38
              }}
            >
              <Download size={14} /> Export CSV
            </button>
            {isAdmin && (
              <button
                onClick={runMonthlyRatings}
                disabled={ratingsRunLoading}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  border: 'none', background: ratingsRunLoading ? '#94a3b8' : '#0f766e',
                  borderRadius: 8, padding: '7px 12px',
                  fontSize: 12, fontWeight: 700, color: 'white',
                  cursor: ratingsRunLoading ? 'not-allowed' : 'pointer',
                  minHeight: 38
                }}
              >
                <Play size={14} />
                {ratingsRunLoading ? 'Running...' : 'Run Monthly Ratings'}
              </button>
            )}
          </div>
        }
      >
        {ratingsError && (
          <div style={{
            marginBottom: 12, padding: '10px 12px', borderRadius: 8,
            border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontSize: 13
          }}>
            {ratingsError}
          </div>
        )}

        {ratingsLoading ? (
          <RatingsTableSkeleton />
        ) : ratingsRows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '28px 10px', color: '#94a3b8', fontSize: 13 }}>
            No monthly ratings found for this month.
          </div>
        ) : (
          <div className="table-wrapper" style={{ overflowX: 'auto' }}>
            <table style={{ minWidth: 940, width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                  <th style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', padding: '10px 8px' }}>Worker</th>
                  <th style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', padding: '10px 8px' }}>Dept</th>
                  <th style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', padding: '10px 8px' }}>Stars</th>
                  <th style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', padding: '10px 8px', textAlign: 'right' }}>Pieces</th>
                  <th style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', padding: '10px 8px', textAlign: 'right' }}>QC Pass %</th>
                  <th style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', padding: '10px 8px', textAlign: 'right' }}>Attendance %</th>
                  <th style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', padding: '10px 8px', textAlign: 'right' }}>Score</th>
                </tr>
              </thead>
              <tbody>
                {ratingsRows.map((row) => (
                  <tr key={row.worker_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 8px', fontWeight: 700, color: '#0f172a', fontSize: 13 }}>{row.worker_name || '-'}</td>
                    <td style={{ padding: '10px 8px', color: '#475569', fontSize: 13 }}>{row.department || '-'}</td>
                    <td style={{ padding: '10px 8px' }}><PartialStars value={row.stars} /></td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', color: '#334155', fontWeight: 600 }}>{Number(row.pieces_completed || 0)}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', color: '#334155' }}>{Number(row.qc_pass_rate || 0).toFixed(2)}%</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', color: '#334155' }}>{Number(row.attendance_rate || 0).toFixed(2)}%</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', color: '#0f766e', fontWeight: 700 }}>{Number(row.score || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        isOpen={isExpiringDocsModalOpen}
        onClose={() => setIsExpiringDocsModalOpen(false)}
        title="Documents Expiring Soon (30 days)"
        maxWidth="860px"
        footer={null}
      >
        {expiringDocsLoading ? (
          <div style={{ padding: 14, color: '#94a3b8' }}>Loading...</div>
        ) : expiringDocs.length === 0 ? (
          <div style={{ padding: 14, color: '#16a34a', fontWeight: 600 }}>No expiring documents in next 30 days.</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Type</th>
                  <th>File</th>
                  <th>Expiry Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {expiringDocs.map((doc) => (
                  <tr key={doc.id}>
                    <td>{doc.employee_name || '-'}</td>
                    <td>{doc.document_type || 'OTHER'}</td>
                    <td style={{ fontSize: 12 }}>{doc.original_filename || '-'}</td>
                    <td>{doc.expiry_date || '-'}</td>
                    <td>
                      <span style={{
                        display: 'inline-flex',
                        background: '#ffedd5',
                        color: '#9a3412',
                        borderRadius: 999,
                        padding: '4px 10px',
                        fontSize: 11,
                        fontWeight: 700
                      }}>
                        {doc.expiry_status || 'EXPIRING_SOON'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Dashboard;
